import type {
  ComponentPropsWithoutRef,
  FunctionComponent,
  ReactNode,
} from 'react'
import React from 'react'

import classnames from 'classnames'

import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './InfoBox.module.scss'
import type { InfoBoxBorderColours } from './InfoBox.utils'
import { infoBoxStyles } from './InfoBox.utils'

export const InfoBox: FunctionComponent<InfoBoxProps> = ({
  className = '',
  heading,
  byline = '',
  borderColour,
  children,
  ...rest
}) => {
  const { colourClass, defaultByline } =
    infoBoxStyles[borderColour] || infoBoxStyles.unstyled

  return (
    <section
      className={classnames(styles.infoBoxWrapper, colourClass, className)}
      data-testid="info-box"
      {...rest}
    >
      <TypographyV2
        className={classnames('sb-margin-bottom-2', styles.infoBoxByline)}
        textStyle="sb-text-interface-body-x-small-regular"
        data-testid="info-box-byline"
      >
        {byline || defaultByline}
      </TypographyV2>
      {heading && (
        <TypographyV2
          tag="h3"
          className="sb-margin-bottom-3"
          textStyle="sb-text-interface-heading-large"
          data-testid="info-box-heading"
        >
          {heading}
        </TypographyV2>
      )}
      {children}
    </section>
  )
}

///////// IMPLEMENTATION /////////

export type InfoBoxProps = {
  /**
   * CSS Class name that can be added for extra styling.
   */
  className?: string
  /**
   * Optional heading which renders inside the box.
   */
  heading?: string
  /**
   * Text appearing at top of component (ie: warning).
   */
  byline?: string
  /**
   * Border colour of InfoBox.
   */
  borderColour: InfoBoxBorderColours
  /**
   * Optional content to be displayed within the component.
   */
  children?: ReactNode
} & ComponentPropsWithoutRef<'section'>
